// Single source of truth for the `YYYY-MM` month key used by the incident
// aggregates — the doc id of `incidentStats/{uid}/months/*` and
// `incidentTotals/months/data/*`.
//
// That key is written by three things that do not share a clock, and read by a
// fourth:
//
//   writer  functions triggers          Cloud Functions, no TZ set  -> UTC
//   writer  bin/rebuild-incident-stats  an operator's laptop        -> their zone
//   writer  bin/seed-*-data             a laptop or a CI container  -> either
//   reader  src/pages/member/Incidents  the member's browser        -> their device
//
// Cut in whatever zone each happens to run in, they disagree about any incident
// in the overlap: a 5pm July 31 call is July locally and August in UTC, so the
// aggregate is filed under a month the reader never looks in. Departments are
// all Pacific today, so the cut is Pacific everywhere, explicitly, regardless of
// where the code runs.
//
// Deliberately the department's zone and not UTC: this key is only ever shown to
// a member as "your July", and a call that a crew attended on the evening of
// July 31 belongs in July on their profile. UTC would be equally deterministic
// but would file those calls in the wrong month for every human reading them.
//
// When departments outside Pacific arrive this becomes a per-tenant setting
// (settings/general), and everything above changes together because it all
// routes through here.
const moment = require('moment-timezone');

const DEPARTMENT_TIMEZONE = 'America/Vancouver';

// `YYYY-MM` for an incident date, cut in the department's zone. Accepts a
// Firestore Timestamp, a JS Date, or a Moment.
const getIncidentMonth = date => {
    if (!date) return null;
    const jsDate = date.toDate ? date.toDate() : date;
    const m = moment(jsDate);
    return m.isValid() ? m.tz(DEPARTMENT_TIMEZONE).format('YYYY-MM') : null;
};

// Every `YYYY-MM` key from `start` to `end` inclusive, so a reader asking for a
// range looks up exactly the docs the writers created.
const getIncidentMonthRange = (start, end) => {
    const from = start && start.toDate ? start.toDate() : start;
    const to = end && end.toDate ? end.toDate() : end;
    if (!from || !to) return [];

    const cursor = moment(from).tz(DEPARTMENT_TIMEZONE).startOf('month');
    const last = moment(to).tz(DEPARTMENT_TIMEZONE).endOf('month');
    if (!cursor.isValid() || !last.isValid()) return [];

    const months = [];
    while (cursor.isSameOrBefore(last)) {
        months.push(cursor.format('YYYY-MM'));
        cursor.add(1, 'month');
    }
    return months;
};

module.exports = {
    DEPARTMENT_TIMEZONE,
    getIncidentMonth,
    getIncidentMonthRange
};
